import React, { useEffect, useRef } from 'react';

import { DataTable, DataTableColumn } from 'cpool-components/general/DataTable';
import { getActiveOrFirstUserId } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { DuplicatesRow } from '~/components/DuplicateModal/DuplicatesTable/DuplicatesRow.tsx';
import { useDuplicateLeadsInfiniteQuery } from '~/components/DuplicateModal/hooks/useDuplicateLeadsInfiniteQuery';
import Spinner from '~/components/icons/Spinner.tsx';

type Props = {
    loanRequestId: string;
};

export const DuplicatesTable: React.FC<Props> = ({ loanRequestId }) => {
    const { t } = useTranslation();
    const {
        data: duplicateLeads,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
    } = useDuplicateLeadsInfiniteQuery(loanRequestId);

    const sentinelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const root = sentinel.closest<HTMLElement>('#search-flyout');
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && hasNextPage) fetchNextPage();
            },
            { root, rootMargin: '0px 0px 100px 0px', threshold: 0 },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, duplicateLeads]);

    if (isFetching && duplicateLeads.length === 0)
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">
                <Spinner />
            </div>
        );
    if (!duplicateLeads || duplicateLeads.length === 0)
        return (
            <div className="w-full inset-0 flex items-center justify-center z-50">{t('DUPLICATE_LIST.EMPTY_DATA')}</div>
        );

    return (
        <div data-testid="duplicate-table">
            <DataTable className="overflow-x-auto scrollbar-thin scrollbar-thumb-rounded-md scrollbar-thumb-dark scrollbar-track-grey-stroke scrollbar-track-rounded-md">
                <DataTableColumn name={t('LEADS.SUMMARY.COLUMNS.CONTACT')} field="name" />
                <DataTableColumn name="Status" field="status" />
                <DataTableColumn name={t('LEADS.SUMMARY.COLUMNS.CREDIT_INFO')} field="creditAmount" />
                <DataTableColumn name={t('LEADS.SUMMARY.COLUMNS.ADVISER')} field="adviser" />
                {duplicateLeads.map((lead) => (
                    <DuplicatesRow
                        key={lead.id}
                        lead={lead}
                        originalLoanRequestId={loanRequestId}
                        payload={{
                            adviser: getActiveOrFirstUserId(lead),
                            creditAmount: lead.finanzbedarf.finanzierungswunsch.kreditbetrag,
                            name: `${lead.antragsteller1.personendaten.vorname} ${lead.antragsteller1.personendaten.nachname}`,
                            status: lead.status,
                        }}
                    />
                ))}
            </DataTable>
            {hasNextPage && <div ref={sentinelRef} className="h-4" />}
            {isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                    <Spinner />
                </div>
            )}
        </div>
    );
};
